import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';

/**
 * Get CSRF token from meta tag (injected by Rails)
 */
export const getCsrfToken = (): string | null => {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || null;
};

/**
 * Axios instance pre-configured with CSRF token handling.
 * Use this for all API calls to Rails backend.
 * 
 * The CSRF token is automatically added to all non-GET requests
 * (POST, PUT, PATCH, DELETE) via the X-CSRF-Token header.
 */
const api: AxiosInstance = axios.create();

// Add request interceptor to include CSRF token
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Only add CSRF token for non-GET requests
  const method = config.method?.toLowerCase();
  if (method && ['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    // Force JSON content-negotiation on writes. axios's default Accept is
    // `application/json, text/plain, */*`; the trailing `*/*` makes Rails fall
    // back to its first registered renderable type (HTML) for any write route
    // that lacks a JSON format default. That hits the action's
    // `format.html { redirect_to … }` branch — often a 302 to a different origin
    // (e.g. a portfolio `<user>.contently.com` subdomain) which an XHR cannot
    // follow, so the promise rejects even though the write already committed
    // ("saved but error" — eng-maintenance#20945). Pinning Accept to JSON routes
    // every write to its `format.json` branch (head :ok / 422 with field errors).
    //
    // axios has ALREADY populated `Accept` with its default wildcard by the time
    // this interceptor runs, so we can't gate on "unset" — we override only when
    // the value is still that default wildcard. A caller that set its own Accept
    // per-call (a distinct string) is left untouched. GETs never reach this branch
    // (some deliberately request text/html or CSV), so they are unaffected.
    config.headers = config.headers || {};
    const AXIOS_DEFAULT_ACCEPT = 'application/json, text/plain, */*';
    const currentAccept = config.headers['Accept'];
    if (!currentAccept || currentAccept === AXIOS_DEFAULT_ACCEPT) {
      config.headers['Accept'] = 'application/json';
    }
  }
  return config;
});

// Add response interceptor for auth failures
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;
      if (status === 401) {
        // Session expired or unauthorized — redirect to signin
        // Avoid redirect loops if we're already on an auth page
        if (!window.location.pathname.startsWith('/signin') &&
            !window.location.pathname.startsWith('/users/')) {
          window.location.href = '/signin';
        }
      }
      // 403 is left to individual components to handle contextually
      // (e.g., show "access denied" message or redirect)
    }
    return Promise.reject(error);
  }
);

/**
 * Re-export isAxiosError for error handling
 */
export const isAxiosError = axios.isAxiosError;
export type { AxiosError };

export default api;
